import React from "react";
import CurrencyDisplay from "../common/CurrencyDisplay";
import placeholder from "../../assets/Car-place-holder-PNG.png";

// Custom scrollbar styles
const scrollbarStyles = `
  .custom-scrollbar-next::-webkit-scrollbar {
    width: 6px;
  }
  
  .custom-scrollbar-next::-webkit-scrollbar-track {
    background: #1e293b;
    border-radius: 3px;
  }
  
  .custom-scrollbar-next::-webkit-scrollbar-thumb {
    background: #475569;
    border-radius: 3px;
  }
  
  .custom-scrollbar-next::-webkit-scrollbar-thumb:hover {
    background: #64748b;
  }
`;

export default function NextInRunList({ vehicles, currentVehicleId }) {
 
  // Filter out current vehicle and get upcoming vehicles
  const upcomingVehicles =
    vehicles?.filter(
      (vehicle) =>
        vehicle.id !== currentVehicleId && vehicle.productAuctionStatusId === 2, // Only upcoming/active vehicles
    ) || [];

  // Sort by run number
  const sortedVehicles = [...upcomingVehicles].sort((a, b) => {
    const runNoA = a?.auctionLanesVehicles?.[0]?.runNo || a?.runNo || 0;
    const runNoB = b?.auctionLanesVehicles?.[0]?.runNo || b?.runNo || 0;
    return runNoA - runNoB;
  });

  // Calculate height for exactly 9 cards
  // Each card is approximately 90px (p-2 + content), plus gap-4 (16px) between cards
  // 9 cards * 90px + 8 gaps * 16px = 810px + 128px = 938px
  const maxVisibleCards = 9;
  const cardHeight = 90; // approximate height in pixels
  const gapHeight = 16; // gap-4 in pixels
  const maxHeight =
    maxVisibleCards * cardHeight + (maxVisibleCards - 1) * gapHeight;

  return (
    <>
      {/* Inject custom scrollbar styles */}
      <style>{scrollbarStyles}</style>

      <div className="bg-[#1e293b] rounded-xl shadow-lg p-4 flex flex-col border border-gray-700/50">
        {/* Header with clock icon */}
        <div className="mb-4 flex items-center gap-2">
          <svg
            className="w-6 h-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h2 className="text-xl font-bold text-white uppercase tracking-wide">
            NEXT IN RUN
          </h2>
        </div>

        {/* Vehicle Count */}
        <div className="text-gray-400 text-sm mb-4">
          {sortedVehicles.length} vehicle
          {sortedVehicles.length !== 1 ? "s" : ""} remaining
        </div>

        {/* Vehicle Cards List - Fixed height for 9 cards with scrollbar if more */}
        <div
          className="overflow-y-auto space-y-4 custom-scrollbar-next pr-2"
          style={{
            maxHeight: `${maxHeight}px`,
            height: sortedVehicles.length > 0 ? "auto" : "400px",
          }}
        >
          {sortedVehicles.length > 0 ? (
            sortedVehicles.map((vehicle) => {
              const lotNumber = vehicle?.lotNumber || "1000018";
              const imageUrl = vehicle?.images?.[0]?.imageUrl || placeholder;
              const vehicleName = vehicle?.name || "Unknown Vehicle";
              const mileage = vehicle?.mileage || 0;
              const exteriorColor = vehicle?.ExteriorColor?.name || "";
              const startingBid = vehicle?.startingBid || 96;
              const runNo =
                vehicle?.runNo ||
                vehicle?.auctionLanesVehicles?.[0]?.runNo ||
                "N/A";

              // Calculate total bids (placeholder - will be dynamic)
              const totalBids = 0;

              // Time remaining for this vehicle (example)
              const timeRemaining = "04:12";

              return (
                <div
                  key={vehicle.id}
                  className="bg-[#1e293b] rounded-lg p-2 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer border border-gray-700/50 flex gap-2"
                >
                  {/* Image Section */}
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 bg-gray-800 rounded-md flex items-center justify-center overflow-hidden border border-gray-700">
                      <img
                        src={imageUrl}
                        alt={vehicleName}
                        className={`w-full h-full object-cover ${imageUrl === placeholder ? "opacity-50" : ""}`}
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.parentElement.innerHTML = `
                          <svg class="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-6a2 2 0 012-2h2a2 2 0 012 2v6m-6 0h6m-6 0H7m12 0h-2m-8-7V7a2 2 0 012-2h2a2 2 0 012 2v3"></path>
                          </svg>
                        `;
                        }}
                      />
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="flex-1 min-w-0 flex flex-col justify-between">
                    <div>
                      {/* Header Row: Lot # and Title */}
                      <div className="flex items-center justify-between gap-3 mb-1">
                        {/* Left Side: Vehicle Name (Takes remaining space) */}
                        <h3
                          className="text-white font-bold text-xs leading-tight truncate flex-1 m-0"
                          title={vehicleName}
                        >
                          {vehicleName}
                        </h3>

                        {/* Right Side: Stacked Run and Lot Numbers */}
                        <div className="flex flex-col gap-1 items-end shrink-0">
                          {/* Run Number */}
                          <div className="inline-block bg-black text-white px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap border border-gray-600/30">
                            Run # {runNo}
                          </div>

                          {/* Lot Number */}
                          <div className="inline-block bg-blue-600/20 text-blue-400 px-1.5 py-0.5 rounded text-[9px] font-bold border border-blue-500/30 whitespace-nowrap">
                            Lot # {lotNumber}
                          </div>
                        </div>
                      </div>

                      {/* Details Row: Mileage & Color */}
                      <div className="text-gray-400 text-[10px] leading-tight truncate">
                        {mileage} mi {exteriorColor && `• ${exteriorColor}`}
                      </div>
                    </div>

                    {/* Bottom Row: Bid & Timer */}
                    <div className="flex items-end justify-between mt-1">
                      {/* Current Bid */}
                      <div>
                        <div className="text-gray-500 text-[8px] uppercase font-semibold">
                          Current Bid
                        </div>
                        <div className="text-white font-bold text-sm leading-none">
                          $ {startingBid.toLocaleString()}
                        </div>
                      </div>

                      {/* Timer */}
                      {/* <div className="text-right">
                       <div className="flex items-center justify-end gap-1">
                        <svg className="w-3 h-3 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                        </svg>
                        <span className="text-red-500 font-bold text-xs">
                          {timeRemaining}
                        </span>
                      </div>
                    </div> */}
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-gray-400">
              <svg
                className="w-16 h-16 mb-4 opacity-50"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                />
              </svg>
              <p className="text-center">No upcoming vehicles</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
